import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Eye } from 'lucide-react';
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import axiosInstance from '../../config/axios.js';
import userService from '../../services/UserService.js';
import { useAuth } from '../../context/AuthContext.js';
import { PageLayout, FormLayout, FormField, Button } from '../common/index.js';

const EditUser = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const currentUserRole = currentUser?.role_name || currentUser?.role?.role_name || currentUser?.role;
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    contact: '',
    city: '',
    location: '',
    dob: '',
    document_type: '',
    document_number: '',
    role_id: '',
    status: 'active',
    monthly_salary: '',
    shipment_commission: '',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [documentAttachment, setDocumentAttachment] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [existingProfilePhoto, setExistingProfilePhoto] = useState(null);
  const [drivingLicenseAttachment, setDrivingLicenseAttachment] = useState(null);
  const [drivingLicensePreview, setDrivingLicensePreview] = useState(null);
  const [existingDrivingLicense, setExistingDrivingLicense] = useState(null);
  const [hasExistingLicense, setHasExistingLicense] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [hasExistingDocument, setHasExistingDocument] = useState(false);
  const [roles, setRoles] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const navigate = useNavigate();

  const documentTypes = ['aadhar', 'pan', 'driving_license', 'voter_id', 'passport'];
  const userStatuses = ['active', 'inactive'];

  useEffect(() => {
    fetchUserAndRoles();
  }, [userId]);

  const fetchUserAndRoles = async () => {
    try {
      const [userRes, rolesRes, hubsRes] = await Promise.all([
        userService.getById(userId),
        axiosInstance.get('/roles'),
        axiosInstance.get('/location-hubs?all=true'),
      ]);

      const userData = userRes.data;
      setFormData(userData);
      setRoles(rolesRes.data.data);
      setLocationHubs(hubsRes.data.hubs || []);

      if (userData.profile_photo_base64) {
        setExistingProfilePhoto(`data:image/jpeg;base64,${userData.profile_photo_base64}`);
      } else {
        setExistingProfilePhoto(userService.getProfilePhoto(userId));
      }

      setExistingDocument(userService.getDocumentAttachment(userId));
      setHasExistingDocument(
        !!(
          userData.document_attachment ||
          userData.document_attachment_base64 ||
          userData.has_document ||
          userData.document_exists
        )
      );

      setExistingDrivingLicense(userService.getDrivingLicenseAttachment(userId));
      setHasExistingLicense(
        !!(
          userData.driving_license_attachment ||
          userData.driving_license_base64 ||
          userData.has_driving_license ||
          userData.driving_license_exists
        )
      );
    } catch (err) {
      Alert.error('Failed to load user data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        Alert.error('Profile photo must be less than 1MB');
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Document must be less than 2MB');
        return;
      }
      setDocumentAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    const fileInput = document.getElementById('profile-photo-input');
    if (fileInput) fileInput.value = '';
  };

  const removeExistingProfilePhoto = () => {
    setExistingProfilePhoto(null);
  };

  const removeDocumentAttachment = () => {
    setDocumentAttachment(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('document-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleDrivingLicenseChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Driving license must be less than 2MB');
        return;
      }
      setDrivingLicenseAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDrivingLicensePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDrivingLicense = () => {
    setDrivingLicenseAttachment(null);
    setDrivingLicensePreview(null);
    const fileInput = document.getElementById('driving-license-input');
    if (fileInput) fileInput.value = '';
  };

  const openDocumentModal = () => {
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const userData = {
        ...formData,
        drivingLicenseAttachment: drivingLicenseAttachment,
        ...(password && { password }),
      };
      await userService.update(userId, userData, profilePhoto, documentAttachment);
      Alert.success('User updated successfully!');
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Edit User" icon={Users}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading user data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const displayPhoto = profilePhotoPreview || existingProfilePhoto;

  return (
    <>
      <FormLayout
        title="Edit User"
        icon={Users}
        onSubmit={handleSubmit}
        submitLabel={submitting ? 'Updating...' : 'Update User'}
        loading={submitting}
        onCancel={() => navigate('/users')}
        maxWidth="max-w-7xl"
      >
        <div>
          <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
            Profile Photo
          </h3>
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border border-gray-300 overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
              {displayPhoto ? (
                <img
                  src={displayPhoto}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
                        <rect width="128" height="128" fill="#e5e7eb"/>
                        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="48" font-weight="bold">
                          ${formData.name ? formData.name.charAt(0).toUpperCase() : 'U'}
                        </text>
                      </svg>
                    `)}`;
                  }}
                />
              ) : (
                <span className="text-3xl sm:text-4xl text-gray-400">👤</span>
              )}
            </div>
            <FormField
              label={
                existingProfilePhoto && !profilePhotoPreview
                  ? 'Update Profile Photo'
                  : 'Upload Profile Photo'
              }
              className="flex-1 w-full"
            >
              <input
                id="profile-photo-input"
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoChange}
                className="input-clean"
              />
              <p className="text-xs text-gray-600 mt-1">Accepted formats: JPG, PNG, GIF, WebP (Max 1MB)</p>
              {profilePhotoPreview && (
                <button
                  type="button"
                  onClick={removeProfilePhoto}
                  className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                >
                  Remove New Photo
                </button>
              )}
              {existingProfilePhoto && !profilePhotoPreview && (
                <button
                  type="button"
                  onClick={removeExistingProfilePhoto}
                  className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                >
                  Remove Existing Photo
                </button>
              )}
            </FormField>
          </div>
        </div>

        <div>
          <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
            <FormField label="Username" required>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="input-clean"
                required
              />
            </FormField>

            <FormField label="Full Name" required>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input-clean"
                required
              />
            </FormField>

            <FormField label="Email" required>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-clean"
                required
              />
            </FormField>

            <FormField label="Date of Birth">
              <input
                type="date"
                name="dob"
                value={formData.dob || ''}
                onChange={handleChange}
                className="input-clean"
              />
            </FormField>
          </div>
        </div>

        <div>
          <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
            Contact Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
            <FormField label="Contact Number" required>
              <input
                type="text"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className="input-clean"
                required
              />
            </FormField>

            <FormField label="Branch" required>
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="input-clean"
                required
              >
                <option value="">Select City</option>
                {locationHubs.map((hub) => (
                  <option key={hub.hub_id} value={hub.location}>
                    {hub.location}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Location" required className="sm:col-span-2">
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="input-clean"
                required
              />
            </FormField>
          </div>
        </div>

        <div>
          <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
            Document Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
            <FormField label="Document Type" required>
              <select
                name="document_type"
                value={formData.document_type}
                onChange={handleChange}
                className="input-clean"
                required
              >
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Document Number" required>
              <input
                type="text"
                name="document_number"
                value={formData.document_number}
                onChange={handleChange}
                className="input-clean"
                required
              />
            </FormField>

            <FormField label="Document Attachment" className="sm:col-span-2">
              {hasExistingDocument && !documentPreview && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon={Eye}
                  onClick={openDocumentModal}
                  className="mb-3"
                >
                  View Existing Document
                </Button>
              )}

              <input
                id="document-attachment-input"
                type="file"
                accept="image/*"
                onChange={handleDocumentAttachmentChange}
                className="input-clean"
              />
              <p className="text-xs text-gray-600 mt-1">
                {hasExistingDocument
                  ? 'Upload new document to replace existing one (Max 2MB)'
                  : 'Upload scanned copy (Max 2MB)'}
              </p>

              {documentPreview && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">New Document Preview:</p>
                  <div className="relative inline-block">
                    <img
                      src={documentPreview}
                      alt="Document Preview"
                      className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={removeDocumentAttachment}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                      title="Remove document"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </FormField>
          </div>
        </div>

        {formData.role_id &&
          roles.find((r) => r.id === parseInt(formData.role_id))?.role_name === 'Driver' && (
            <div>
              <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
                Driving License
              </h3>
              <FormField label="Driving License Attachment" className="sm:col-span-2">
                {hasExistingLicense && !drivingLicensePreview && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon={Eye}
                    onClick={() => window.open(existingDrivingLicense, '_blank')}
                    className="mb-3"
                  >
                    View Existing License
                  </Button>
                )}

                <input
                  id="driving-license-input"
                  type="file"
                  accept="image/*"
                  onChange={handleDrivingLicenseChange}
                  className="input-clean"
                />
                <p className="text-xs text-gray-600 mt-1">
                  {hasExistingLicense
                    ? 'Upload new license to replace existing one (Max 2MB)'
                    : 'Upload driving license (Max 2MB)'}
                </p>

                {drivingLicensePreview && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">New License Preview:</p>
                    <div className="relative inline-block">
                      <img
                        src={drivingLicensePreview}
                        alt="License Preview"
                        className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                      />
                      <button
                        type="button"
                        onClick={removeDrivingLicense}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                        title="Remove license"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </FormField>
            </div>
          )}

        <div>
          <h3 className="text-base font-semibold text-tertiary mb-4 pb-2 border-b border-gray-100">
            Role and Status
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
            <FormField label="Role" required>
              <select
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                className="input-clean"
                required
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Monthly Salary (₹)">
              <input
                type="text"
                name="monthly_salary"
                value={formData.monthly_salary || ''}
                onChange={handleChange}
                placeholder="Enter monthly salary"
                className="input-clean"
              />
            </FormField>

            <FormField label="Shipment Commission %">
              <input
                type="number"
                name="shipment_commission"
                value={formData.shipment_commission ?? ''}
                onChange={handleChange}
                placeholder="0"
                min="0"
                max="100"
                className="input-clean"
              />
            </FormField>

            {currentUserRole === 'System Admin' && (
              <FormField label="Password">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password (min 8 chars)"
                  minLength={8}
                  className="input-clean"
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing password</p>
              </FormField>
            )}

            <FormField label="Status">
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-clean"
              >
                {userStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.toUpperCase()}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </div>
      </FormLayout>

      {hasExistingDocument && (
        <DocumentModal
          isOpen={isDocModalOpen}
          onClose={closeDocumentModal}
          documentUrl={existingDocument}
          documentType={formData.document_type}
        />
      )}
    </>
  );
};

export default EditUser;
